// Copyright 2025 Sorama B.V.
// Manager for handling package and license operations for CamIV64 devices
import { DialogService } from "aurelia-dialog";
import { ICurrentlyActivatedPackage } from "../controls/package-badge/PackageBadge";
import { ActivatedLicenseDTO, LicenseBundle, SellableLicense } from "../datatypes/SoramaWebApi";
import { CamIv64Device } from "../services/Hardware/CamHTTP/CamIV64Helpers";
import { ICamApi } from "../services/Hardware/CamHTTP/ICamApi";
import { ILicenseService } from "../services/License/ILicenseService";
import { ILicenseBundleService } from "../services/LicenseBundle/ILicenseBundleService";

export type LoadedLicenseState = {
    installablePackages: LicenseBundle[];
    devicePackages: ICurrentlyActivatedPackage[];
    installedLicenses: ActivatedLicenseDTO[];
    missingLicenses: ActivatedLicenseDTO[];
    isLoadingPackages: boolean;
};

export class CamIV64PackageLicenseManager {
    constructor(
        private readonly licenseService: ILicenseService,
        private readonly licenseBundleService: ILicenseBundleService,
        private readonly camApi: ICamApi,
        private readonly dialogService: DialogService
    ) {}

    public async loadInstalledLicenses(selectedCamIV64: CamIv64Device): Promise<LoadedLicenseState> {
        let licenseTagsOnDevice: string[] = [];
        let licensesOnPortal: ActivatedLicenseDTO[] = [];
        let licenseBundlesOnPortal: LicenseBundle[] = [];

        const promises = [
            this.licenseService
                .getActivatedLicensesForDevice(selectedCamIV64.serialNumber)
                .then((result) => {
                    const currentDate = new Date();
                    licensesOnPortal = result.filter((license: ActivatedLicenseDTO) => {
                        return currentDate < new Date(license.DateEnd);
                    });
                })
                .catch((err) => console.error("Error fetching licenses from portal", err)),

            this.licenseBundleService
                .getOwnedLicenseBundlesForDevice(selectedCamIV64.serialNumber)
                .then((result) => {
                    const currentDate = new Date();
                    licenseBundlesOnPortal = result.filter((bundle: LicenseBundle) => {
                        return (
                            currentDate < new Date(bundle.ExpirationDate) &&
                            currentDate >= new Date(bundle.ActivationDate)
                        );
                    });
                })
                .catch((err) => console.error("Error fetching license bundles from portal", err)),

            this.camApi
                .getLicenseTagsOnDevice()
                .then((result) => (licenseTagsOnDevice = result ?? []))
                .catch((err) => console.error("Error fetching licenses on device", err)),
        ];

        await Promise.all(promises);

        return {
            installablePackages: licenseBundlesOnPortal.filter(
                (bundle) => !licenseTagsOnDevice.includes(bundle.LicenseBundleKind?.Tag)
            ),
            devicePackages: licenseBundlesOnPortal
                .filter((bundle) => licenseTagsOnDevice.includes(bundle.LicenseBundleKind?.Tag))
                .map((bundle) => this.createPackageFromBundle(bundle)),
            installedLicenses: licensesOnPortal.filter((license) =>
                licenseTagsOnDevice.includes(license.LicenseKind?.LicenseKindTag)
            ),
            missingLicenses: licensesOnPortal.filter(
                (license) => !licenseTagsOnDevice.includes(license.LicenseKind?.LicenseKindTag)
            ),
            isLoadingPackages: false,
        };
    }

    public createPackageFromBundle(bundle: LicenseBundle): ICurrentlyActivatedPackage {
        const kind = bundle.LicenseBundleKind;
        const activationDate = new Date(bundle.ActivationDate);
        const expirationDate = new Date(bundle.ExpirationDate);
        const isLifetime =
            expirationDate && activationDate
                ? expirationDate.getFullYear() - activationDate.getFullYear() >= 100
                : false;

        return {
            name: kind?.Name || "",
            tier: kind?.Tier || "",
            tag: kind?.Tag || "",
            expirationDate: expirationDate,
            isLifetime: isLifetime,
        };
    }

    public async activateLicense(license: SellableLicense, selectedCamIV64: CamIv64Device): Promise<void> {
        const hardwareClock = await this.camApi.getHardwareClock();
        // Get activated license from backend
        const encryptedLicense = await this.licenseService
            .activateFeature(license.Id, selectedCamIV64.serialNumber, hardwareClock)
            .catch((e) => {
                throw new Error("Could not activate the feature license: " + e.message);
            });
        await this.camApi.sendLicenseToDevice(encryptedLicense);
    }

    public async sendPackageFileToDevice(selectedCamIV64: CamIv64Device): Promise<void> {
        if (!selectedCamIV64) return;
        try {
            const deviceId = selectedCamIV64.serialNumber;
            const packageFile = await this.licenseBundleService.getPackageFile(deviceId);
            if (packageFile) {
                await this.camApi.sendPackageFileToDevice(packageFile);
            }
        } catch (error) {
            console.error("Failed to send package.package file to device:", error);
        }
    }

    public async installPackage(installablePackage: LicenseBundle, selectedCamIV64: CamIv64Device): Promise<void> {
        if (installablePackage.DeviceId != selectedCamIV64.serialNumber) {
            throw new Error("Package cannot be activated on the selected device");
        }
        const hardwareClock = await this.camApi.getHardwareClock();
        const signedLicense = await this.licenseBundleService.getSignedLicenseBundle(
            installablePackage.Id,
            hardwareClock
        );
        await this.camApi.sendLicenseToDevice(signedLicense);
        const deviceId = selectedCamIV64.serialNumber;
        const packageFile = await this.licenseBundleService.getPackageFile(deviceId);
        await this.camApi.sendPackageFileToDevice(packageFile);
    }
}
